import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import useDownloadLogs from '@/redux/downloadLog/useDownloadLogs';
import { IDownloadLog } from './downloadLog.d';

function DownloadLogsTable() {
  const { logs, isLoading, error, hasMore, lastLogElementRef } =
    useDownloadLogs(10);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid date';
    }
  };

  const getResourceTypeColor = (resourceType: string) => {
    switch (resourceType.toLowerCase()) {
      case 'model':
        return 'bg-blue-100 text-blue-800';
      case 'file':
        return 'bg-green-100 text-green-800';
      case 'image':
        return 'bg-purple-100 text-purple-800';
      case 'document':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading download logs
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error?.data?.message ||
                'An error occurred while loading the download logs.'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-white shadow-sm">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="max-w-[200px]">File Name</TableHead>
            <TableHead className="max-w-[200px]">Project Title</TableHead>
            <TableHead className="max-w-[100px]">Resource Type</TableHead>
            <TableHead className="max-w-[150px]">Downloaded At</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log: IDownloadLog, index: number) => (
            <TableRow
              key={log._id}
              ref={index === logs.length - 1 ? lastLogElementRef : undefined}
              className="hover:bg-gray-50"
            >
              <TableCell className="font-medium">
                <div className="max-w-[300px] truncate" title={log.fileName}>
                  {log.fileName}
                </div>
              </TableCell>
              <TableCell>
                <div className="max-w-[300px] truncate" title={log.title}>
                  {log.title}
                </div>
              </TableCell>

              <TableCell>
                <Badge
                  variant="secondary"
                  className={`text-xs ${getResourceTypeColor(log.resourceType)}`}
                >
                  {log.resourceType}
                </Badge>
              </TableCell>

              <TableCell className="text-sm text-gray-600">
                {formatDate(log.createdAt)}
              </TableCell>
            </TableRow>
          ))}

          {/* Loading skeleton for infinite scroll */}
          {isLoading && hasMore && (
            <>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell>
                    <Skeleton className="h-4 w-[180px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[130px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-6 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-6 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-6 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[120px]" />
                  </TableCell>
                </TableRow>
              ))}
            </>
          )}
        </TableBody>
      </Table>

      {/* Empty state */}
      {!isLoading && logs.length === 0 && (
        <div className="p-8 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No download logs
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            You haven&apos;t downloaded any files yet. Your download history
            will appear here.
          </p>
        </div>
      )}

      {/* End of data message */}
      {!isLoading && !hasMore && logs.length > 0 && (
        <div className="border-t bg-gray-50 px-4 py-3 text-center">
          <p className="text-sm text-gray-600">
            You&apos;ve reached the end of your download history.
          </p>
        </div>
      )}
    </div>
  );
}

export default DownloadLogsTable;
